import React, { useEffect, useRef } from "react";
import {
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from "@mui/material";
import { useRecoilState, useRecoilValue } from "recoil";
import { GetTypographyContent } from "../shared/Typography/Typography";
import { RadioFormControl } from "./NewRequisition.styles";
import {
  currentNewRequisition,
  currentNewRequisitionEdit,
  providerState,
} from "../../recoil/atoms";
import { NewRequisitionForm } from "../../recoil/types";
import { BillingType } from "./types";
import {
  getBillingType,
  getBillingParameter,
  getBillToClientText,
} from "./helpers";

import NewReqBillToInsurance from "./NewReqBillToInsurance";
import NewReqBillToPatient from "./NewReqBillToPatient";
import NewReqBillTreatmentNotes from "./NewReqBillTreatmentNotes";
import { useMirrorRequisitionToCreateOrderState } from "./useMirrorRequisitionToCreateOrderState";

const NewReqBillingInformation: React.FC<{
  preflight: boolean;
  submittedRequisition?: NewRequisitionForm;
  isError?: boolean;
}> = ({ preflight = false, submittedRequisition, isError }) => {
  const [auth] = useRecoilState(providerState);
  const newGlobalRequisition = useRecoilValue(currentNewRequisition);

  const [newRequisition, setNewRequisition] = useRecoilState(
    currentNewRequisitionEdit,
  );
  const { requisitionForm } = newRequisition;

  useEffect(() => {
    setNewRequisition({
      preflight,
      isError,
      requisitionForm: submittedRequisition || newGlobalRequisition,
    });
  }, [isError, preflight]);

  useMirrorRequisitionToCreateOrderState({
    enabled: !preflight,
    requisitionForm,
  });

  useEffect(() => {
    const anchorEl = document.getElementById(
      "treatmentNotesAccordion-help",
    ) as HTMLDivElement;
    if (anchorEl && (preflight || requisitionForm.editTreatmentNotes)) {
      anchorEl.click();
    }
  }, []);

  const notesFieldReference = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (
      requisitionForm.editTreatmentNotes &&
      notesFieldReference.current &&
      requisitionForm.treatmentNotes
    ) {
      notesFieldReference.current.focus();
      notesFieldReference.current.selectionStart =
        requisitionForm.treatmentNotes.length;
      notesFieldReference.current.selectionEnd =
        requisitionForm.treatmentNotes.length;
    }
  }, [requisitionForm.editTreatmentNotes]);

  useEffect(() => {
    if (
      requisitionForm.billToInsurance &&
      requisitionForm.relationshipToPolicyOwnerSelf &&
      (requisitionForm.policyOwnerFirstName !== requisitionForm.firstName ||
        requisitionForm.policyOwnerLastName !== requisitionForm.lastName ||
        requisitionForm.policyOwnerDob !== requisitionForm.dateOfBirth)
    ) {
      setNewRequisition({
        ...newRequisition,
        requisitionForm: {
          ...newRequisition.requisitionForm,
          policyOwnerFirstName: requisitionForm.firstName,
          policyOwnerLastName: requisitionForm.lastName,
          policyOwnerDob: requisitionForm.dateOfBirth,
        },
      });
    }
  }, [requisitionForm]);

  const isDisable = requisitionForm.insuranceSheetAttached
    ? { opacity: 0.35 }
    : { opacity: 1 };

  const noBillingOptionSelected =
    !requisitionForm.billToInsurance &&
    !requisitionForm.billToClient &&
    !requisitionForm.billToPatient &&
    !requisitionForm.billToPendingMedicaid;
  const effectiveIsError = isError ?? newRequisition.isError;
  const billingError = effectiveIsError && noBillingOptionSelected;

  return (
    <>
      <Grid container spacing={3} sx={isDisable}>
        <Grid item xs={12} sm={12} md={12}>
          <RadioFormControl $hideBorder error={billingError}>
            <RadioGroup
              aria-label="Billing option"
              value={getBillingType(
                requisitionForm.billToInsurance,
                requisitionForm.billToClient,
                requisitionForm.billToPatient,
                requisitionForm.billToPendingMedicaid,
              )}
              onChange={(e) => {
                const selectedBillingType = e.target.value as BillingType;
                setNewRequisition({
                  ...newRequisition,
                  requisitionForm: {
                    ...newRequisition.requisitionForm,
                    billToInsurance: false,
                    billToPatient: false,
                    billToClient: false,
                    billToPendingMedicaid: false,
                    billToCashPay: false,
                    [getBillingParameter(selectedBillingType)]: true,
                    ...(selectedBillingType === BillingType.Insurance &&
                    !requisitionForm.billToInsuranceType
                      ? { billToInsuranceType: "Entry" as const }
                      : {}),
                  },
                });
              }}
              row
            >
              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  value={BillingType.Insurance}
                  control={<Radio />}
                  label={GetTypographyContent({
                    content: "Bill to Insurance",
                    size: "medium",
                    textColor: "greyMedium",
                  })}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  value={BillingType.Patient}
                  control={<Radio />}
                  label={GetTypographyContent({
                    content: "Bill to Patient",
                    size: "medium",
                    textColor: "greyMedium",
                  })}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  value={BillingType.Client}
                  control={<Radio />}
                  label={GetTypographyContent({
                    content: getBillToClientText(auth),
                    size: "medium",
                    textColor: "greyMedium",
                  })}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  value={BillingType.PendingMedicaid}
                  control={<Radio />}
                  label={GetTypographyContent({
                    content: "Pending Medicaid",
                    size: "medium",
                    textColor: "greyMedium",
                  })}
                />
              </Grid>
            </RadioGroup>
            {billingError && (
              <FormHelperText>Select a billing option</FormHelperText>
            )}
          </RadioFormControl>
        </Grid>
      </Grid>
      {requisitionForm.billToInsurance && (
        <NewReqBillToInsurance preflight={preflight} />
      )}

      {requisitionForm.billToPatient && <NewReqBillToPatient />}

      {requisitionForm.dateOfBirth &&
        !requisitionForm.isInternationalAccount && <NewReqBillTreatmentNotes />}
    </>
  );
};
export default NewReqBillingInformation;
